type TelemetryStatus = 'success' | 'failure';

export type TelemetryAuthFlow = 'cognito_oauth' | 'cognito_password' | 'cached_session' | 'unknown';
export type TelemetryAuthSource = 'sso' | 'password' | 'cache_restore' | 'token_refresh' | 'unknown';

export interface TelemetryAuthContext {
  sessionId: string;
  authFlow: TelemetryAuthFlow;
  authSource: TelemetryAuthSource;
  userSub?: string | null;
  userEmail?: string | null;
  userPrincipal?: string | null;
  originAuthFlow?: TelemetryAuthFlow;
  establishedAt: string;
}

export interface TelemetryEventInput {
  userSub?: string | null;
  userEmail?: string | null;
  userGroups?: string[];
  authToken?: string | null;
  actionType: string;
  actionTarget: string;
  status: TelemetryStatus;
  latencyMs?: number;
  requestId?: string;
  extra?: Record<string, unknown>;
}

const TELEMETRY_AUTH_CONTEXT_KEY = 'hcrc.telemetry.auth_context';

const parseTelemetryAuthFlow = (value: unknown): TelemetryAuthFlow => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'cognito_oauth') return 'cognito_oauth';
  if (normalized === 'cognito_password') return 'cognito_password';
  if (normalized === 'cached_session') return 'cached_session';
  return 'unknown';
};

const parseTelemetryAuthSource = (value: unknown): TelemetryAuthSource => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'sso') return 'sso';
  if (normalized === 'password') return 'password';
  if (normalized === 'cache_restore') return 'cache_restore';
  if (normalized === 'token_refresh') return 'token_refresh';
  return 'unknown';
};

export const readTelemetryAuthContext = (): TelemetryAuthContext | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(TELEMETRY_AUTH_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const sessionId = String((parsed as any).sessionId || '').trim();
    if (!sessionId) return null;

    return {
      sessionId,
      authFlow: parseTelemetryAuthFlow((parsed as any).authFlow),
      authSource: parseTelemetryAuthSource((parsed as any).authSource),
      userSub: ((parsed as any).userSub ?? null) as string | null,
      userEmail: ((parsed as any).userEmail ?? null) as string | null,
      userPrincipal: ((parsed as any).userPrincipal ?? null) as string | null,
      originAuthFlow: parseTelemetryAuthFlow((parsed as any).originAuthFlow),
      establishedAt: String((parsed as any).establishedAt || ''),
    };
  } catch {
    return null;
  }
};

export const writeTelemetryAuthContext = (context: TelemetryAuthContext): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(TELEMETRY_AUTH_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // no-op
  }
};

export const clearTelemetryAuthContext = (): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(TELEMETRY_AUTH_CONTEXT_KEY);
  } catch {
    // no-op
  }
};

const getEnvironment = (): string => {
  const env = (process.env.REACT_APP_ENV || process.env.REACT_APP_STAGE || process.env.NODE_ENV || 'unknown').toLowerCase();
  if (env.includes('prod')) return 'prod';
  if (env.includes('dev')) return 'dev';
  return env;
};

const getEndpoint = (): string | null => {
  const telemetryDisabled = (process.env.REACT_APP_TELEMETRY_DISABLED ?? '').trim().toLowerCase() === 'true';
  if (telemetryDisabled) {
    return null;
  }

  const telemetryApiUrl = process.env.REACT_APP_TELEMETRY_API_URL?.trim();
  if (telemetryApiUrl) {
    return telemetryApiUrl;
  }

  const telemetryEndpoint = process.env.REACT_APP_TELEMETRY_ENDPOINT?.trim();
  if (telemetryEndpoint) {
    return telemetryEndpoint;
  }

  const amaV2ApiUrl = process.env.REACT_APP_AMA_V2_API_URL?.trim();
  if (amaV2ApiUrl) {
    return `${amaV2ApiUrl.replace(/\/+$/, '')}/telemetry/events`;
  }

  return null;
};

const generateRequestId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // no-op
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getUserGroupsFromIdToken = (idToken?: string | null): string[] => {
  try {
    if (!idToken) return [];
    const payload = JSON.parse(atob(idToken.split('.')[1] || ''));
    const groups = payload?.['cognito:groups'];
    if (Array.isArray(groups)) {
      return groups.map((g) => String(g));
    }
  } catch {
    // no-op
  }
  return [];
};

export const trackTelemetryEvent = async (event: TelemetryEventInput): Promise<void> => {
  const endpoint = getEndpoint();
  if (!endpoint) return;

  const authContext = readTelemetryAuthContext();
  const extra = event.extra || {};
  const extraAuthFlow = String(extra['auth_flow'] || '').trim();
  const extraAuthSource = String(extra['auth_source'] || '').trim();
  const extraSessionId = String(extra['session_id'] || '').trim();
  const extraUserPrincipal = String(extra['user_principal'] || '').trim();
  const extraOriginAuthFlow = String(extra['auth_origin_flow'] || '').trim();

  const payload = {
    timestamp: new Date().toISOString(),
    environment: getEnvironment(),
    user_sub: event.userSub || authContext?.userSub || null,
    user_email: event.userEmail || authContext?.userEmail || null,
    user_groups: event.userGroups || [],
    user_principal: extraUserPrincipal || authContext?.userPrincipal || null,
    action_type: event.actionType,
    action_target: event.actionTarget,
    status: event.status,
    latency_ms: event.latencyMs,
    request_id: event.requestId || generateRequestId(),
    session_id: extraSessionId || authContext?.sessionId || null,
    auth_flow: extraAuthFlow || authContext?.authFlow || null,
    auth_source: extraAuthSource || authContext?.authSource || null,
    auth_origin_flow: extraOriginAuthFlow || authContext?.originAuthFlow || null,
    ...extra,
  };

  const dispatch = () => {
    try {
      const body = JSON.stringify(payload);
      const hasAuthToken = Boolean(event.authToken);
      if (!hasAuthToken && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        const sent = navigator.sendBeacon(endpoint, blob);
        if (sent) {
          return;
        }
      }

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (event.authToken) {
        headers.Authorization = event.authToken;
      }

      fetch(endpoint, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      }).catch(() => {
        // Intentionally swallow errors to keep telemetry non-blocking.
      });
    } catch {
      // Intentionally swallow errors to keep telemetry non-blocking.
    }
  };

  // Schedule asynchronously off the critical path to prevent UI/network contention
  if (typeof window !== 'undefined' && typeof (window as any).requestIdleCallback === 'function') {
    (window as any).requestIdleCallback(dispatch, { timeout: 2000 });
  } else {
    setTimeout(dispatch, 0);
  }
};
